/**
 * Validate schema.
 *
 * @param {Object} schema
 * @private
 */

export function validate(schema) {
  return schema;
}

/**
 * Cast prismic object against given schema.
 *
 * @param {Object} obj
 * @param {Object} schema
 * @private
 */

export function cast(obj, schema = {}) {
  const result = {};
  const validator = flatten(schema);
  // @todo we should create validator from a merge between schema and obj (we will try to guess the
  //  schema from obj, ex tesxt being arrays)
  Object.keys(obj).forEach((key) => {
    result[key] = transform(validator, obj[key], key);
  });
  return result;
}

/**
 * Flatten schema into an object similar to a prismic result.
 *
 * Examples:
 *
 *   transform({hero: {title: {type: 'text'}}})
 *   // => {hero_title: {type: 'text'}}
 *
 * @param {Object} obj
 * @return {Object}
 * @private
 */

function flatten(obj) {
  const result = {};
  traverse(obj, result);
  return result;
}

/**
 * Traverse object recursively.
 * @private
 */

function traverse(parent, target, key) {
  Object.keys(parent).forEach((name) => {
    const child = parent[name];
    const { type, subtype, ...props } = child;
    const k = key == null ? name : `${key}_${name}`;
    if (type && typeof type === 'string') {
      // eslint-disable-next-line no-param-reassign
      target[k] = { type, subtype };
      traverse(props, target, k);
    } else if (typeof child === 'object') {
      traverse(child, target, k);
    }
  });
}

/**
 * Transform value according
 *
 * @param {Object} fields schema
 * @param {Any} value
 * @param {String?} key used for slice/group
 * @return {Any}
 * @private
 */

function transform(fields = {}, value, key) {
  switch (getFieldType(fields[key])) {
    case 'string':
    case 'text':
    case 'keytext':
    case 'unstructuredtext':
      return text(value);
    case 'image':
      return image(value);
    case 'slice':
      return slice(fields, value, key);
    case 'group':
      return group(fields, value, key);
    default:
      // object, array and others
      return value;
  }
}

/**
 * Get field type.
 *
 * A field should always have a property type and an optional subtype.
 *
 * Example:
 *
 *   getFieldType({type: String})
 *   // => string
 *   getFieldType({type: String, subtype: 'unstructuredtext})
 *   // => unstructuredtext
 *
 * @param {Object} field
 * @return {String}
 * @private
 */

function getFieldType(field = {}) {
  const { type = '', subtype } = field;
  return subtype || type.toLowerCase();
}

/**
 * Cast value into a string.
 * A text in prismic can be a string or an unstructured text. An unstructured text
 * can be null, an empty array or an array with an text item.
 *
 * Examples:
 *
 *   text('hello')
 *   // => 'hello'
 *   text(null)
 *   // => ''
 *   text([])
 *   // => ''
 *   text([{text: 'hello'}])
 *   // => 'hello'
 *
 * @private
 */

function text(value) {
  if (typeof value === 'string') return value;
  if (value instanceof Array && value.length > 0) {
    return value[0].text;
  }
  return '';
}

/**
 * Cast value into an image.
 *
 * @param {Any} value
 * @return {Object}
 * @private
 */

function image(value) {
  let result = { alt: '', url: '' };
  if (typeof value === 'object') result = { ...result, ...value };
  return result;
}

/**
 * Cast value into a slice.
 *
 * @param {Object} fields used for casting
 * @param {Any} value
 * @param {String} key
 * @return {Object}
 * @private
 */

function slice(fields, value, key) {
  if (value instanceof Array) {
    return value.map((obj) => {
      const { primary = {}, items = [] } = obj;
      return {
        ...substring(fields, primary, key),
        items: items.map((item) => substring(fields, item, key, '_items_')),
      };
    });
  }
  return [];
}

/**
 * Cast value into an array group/
 *
 * @param {Object} fields used for casting
 * @param {Any} value
 * @param {String} key
 * @return {Array}
 * @private
 */

function group(fields, value, key) {
  if (value instanceof Array) {
    return value.map((obj) => substring(fields, obj, key));
  }
  return [];
}

/**
 * Substring name from an object properties.
 *
 * Examples:
 *
 *   substring({ hero_title: 'hello' }, 'hero')
 *   // => { title: 'hello' }
 *
 * @param {Object} fields used for casting
 * @param {Object} obj
 * @param {String} name
 * @param {String?} separator (used for slice items)
 * @return {Object}
 * @private
 */

function substring(fields, obj, name, separator = '_') {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const l = name.length + 1;
    if (key.substring(0, l) === (`${name}_`)) {
      const k = key.substring(l);
      result[k] = transform(fields, value, name + separator + k);
    } else {
      result[key] = value;
    }
  });
  return result;
}
